const DEFAULT_PC_AUTH_BASE_URL = 'https://auth.phishcloud.io';
const DEFAULT_PENDING_STATE_TTL_MS = 10 * 60 * 1000;
const TOKEN_STORAGE_KEY = 'pcAuthTokens';
const USER_STORAGE_KEY = 'pcAuthUser';
const PENDING_STATES_STORAGE_KEY = 'pcAuthPendingStates';
const LEGACY_PENDING_STATE_STORAGE_KEY = 'pcAuthPendingState';
const SESSION_LOGIN_AT_KEY = 'session_login_at';
const STRICT_SESSION_MAX_MS = 24 * 60 * 60 * 1000;
const INTENDED_ROUTE_KEY = 'intendedRoute';
const DEFAULT_POST_LOGIN_ROUTE = '/Dashboard/History';
const CONSUMED_STATE_CACHE_TTL_MS = 10 * 60 * 1000;
const EXCHANGED_CODE_CACHE_TTL_MS = 2 * 60 * 1000;
let refreshInFlightPromise = null;
const consumedStateCache = new Map();
const exchangedCodeResultCache = new Map();
const exchangeCodeInFlightCache = new Map();

const normalizeEmail = (value) => (value || '').trim().toLowerCase();

const normalizeBaseUrl = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
};

const getPcAuthBaseUrl = () =>
  normalizeBaseUrl(process.env.REACT_APP_PC_AUTH_BASE_URL) || DEFAULT_PC_AUTH_BASE_URL;

const resolveAbsoluteUrl = (value, fallbackPath) => {
  const configured = (value || '').trim();
  const resolved = configured || fallbackPath;

  if (!resolved) {
    return '';
  }

  if (resolved.startsWith('http://') || resolved.startsWith('https://')) {
    return resolved;
  }

  const baseUrl = getPcAuthBaseUrl();
  if (baseUrl) {
    return `${baseUrl}${resolved.startsWith('/') ? resolved : `/${resolved}`}`;
  }

  return new URL(resolved, window.location.origin).toString();
};

const getPcAuthRedirectUri = () =>
  (process.env.REACT_APP_PC_AUTH_REDIRECT_URI || '').trim() || `${window.location.origin}/auth/callback`;

const getDefaultTenant = () => (process.env.REACT_APP_PC_AUTH_TENANT || 'phishcloud').trim().toLowerCase();

const inferTenant = (domain) => {
  const normalizedDomain = (domain || '').trim().toLowerCase();
  if (normalizedDomain === 'duracell.com') {
    return 'duracell';
  }
  if (normalizedDomain === 'phishcloudinc.onmicrosoft.com') {
    return 'phishcloud';
  }
  return getDefaultTenant();
};

const getProviderSlug = (provider) => {
  const normalized = (provider || '').trim().toLowerCase();
  if (normalized === 'microsoft' || normalized === 'azure') {
    return 'azure';
  }
  if (normalized === 'google') {
    return 'google';
  }
  return '';
};

const makeStateToken = () => {
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const safeJsonParse = (rawValue, fallbackValue) => {
  if (!rawValue) {
    return fallbackValue;
  }
  try {
    return JSON.parse(rawValue);
  } catch (error) {
    return fallbackValue;
  }
};

const safeStorageGetItem = (storage, key) => {
  try {
    return storage?.getItem(key) || '';
  } catch (error) {
    return '';
  }
};

const safeStorageSetItem = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch (error) {
    // Best effort storage write.
  }
};

const safeStorageRemoveItem = (storage, key) => {
  try {
    storage?.removeItem(key);
  } catch (error) {
    // Best effort storage cleanup.
  }
};

// Auth tokens/user are persisted in localStorage so a login survives browser
// restarts for the full 24-hour session window. sessionStorage copies are
// migrated upward (legacy) and then cleared so we do not keep dual sources.
const readAuthSessionItem = (key) => {
  const localValue = safeStorageGetItem(localStorage, key);
  if (localValue) {
    // Clear any stale sessionStorage copy to avoid split-brain reads.
    safeStorageRemoveItem(sessionStorage, key);
    return localValue;
  }

  // Legacy migration: older builds stored tokens only in sessionStorage.
  const sessionValue = safeStorageGetItem(sessionStorage, key);
  if (sessionValue) {
    safeStorageSetItem(localStorage, key, sessionValue);
    safeStorageRemoveItem(sessionStorage, key);
  }
  return sessionValue;
};

const writeAuthSessionItem = (key, value) => {
  safeStorageSetItem(localStorage, key, value);
  safeStorageRemoveItem(sessionStorage, key);
};

const removeAuthSessionItem = (key) => {
  safeStorageRemoveItem(localStorage, key);
  safeStorageRemoveItem(sessionStorage, key);
};

const readSessionLoginAtMs = () => {
  const rawValue = localStorage.getItem(SESSION_LOGIN_AT_KEY);
  const parsedValue = Number(rawValue || 0);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
};

export const markSessionLoginAtNow = () => {
  localStorage.setItem(SESSION_LOGIN_AT_KEY, Date.now().toString());
};

const clearSessionLoginAt = () => {
  localStorage.removeItem(SESSION_LOGIN_AT_KEY);
};

export const isStrictSessionExpired = () => {
  const sessionLoginAtMs = readSessionLoginAtMs();
  if (!sessionLoginAtMs) {
    return true;
  }
  return Date.now() - sessionLoginAtMs >= STRICT_SESSION_MAX_MS;
};

const isLocalhostRuntime = () => ['localhost', '127.0.0.1'].includes(window.location.hostname);

const resolvePcAuthApiUrl = (fallbackPath) => {
  // Always route through the portal's own origin so requests are same-origin.
  // In development, the CRA dev proxy forwards /auth/* to pc-auth.
  // In production, nginx proxies /auth/* to pc-auth.
  // This eliminates cross-origin cold-start latency (DNS + TLS handshake)
  // that caused the first SSO attempt to exceed PrivateRoute's auth-load window.
  return new URL(fallbackPath, window.location.origin).toString();
};

const normalizePendingStates = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.filter((entry) => entry && typeof entry === 'object' && typeof entry.state === 'string');
  }
  if (typeof value === 'object' && typeof value.state === 'string') {
    return [value];
  }
  return [];
};

const readPendingStates = (storage) => {
  const next = normalizePendingStates(safeJsonParse(storage.getItem(PENDING_STATES_STORAGE_KEY), null));
  if (next.length > 0) {
    return next;
  }

  return normalizePendingStates(safeJsonParse(storage.getItem(LEGACY_PENDING_STATE_STORAGE_KEY), null));
};

const writePendingStates = (storage, states) => {
  if (!states || states.length === 0) {
    storage.removeItem(PENDING_STATES_STORAGE_KEY);
    storage.removeItem(LEGACY_PENDING_STATE_STORAGE_KEY);
    return;
  }

  storage.setItem(PENDING_STATES_STORAGE_KEY, JSON.stringify(states));
  storage.removeItem(LEGACY_PENDING_STATE_STORAGE_KEY);
};

const prunePendingStates = (states) => {
  const now = Date.now();
  return states.filter((entry) => now - Number(entry.createdAt || 0) <= DEFAULT_PENDING_STATE_TTL_MS);
};

const pruneConsumedStateCache = () => {
  const now = Date.now();
  consumedStateCache.forEach((entry, key) => {
    if (!entry || now - Number(entry.createdAt || 0) > CONSUMED_STATE_CACHE_TTL_MS) {
      consumedStateCache.delete(key);
    }
  });
};

const readConsumedStateCache = (state) => {
  pruneConsumedStateCache();
  return consumedStateCache.get(state);
};

const writeConsumedStateCache = (state, returnTo) => {
  consumedStateCache.set(state, {
    returnTo: (returnTo || '').trim(),
    createdAt: Date.now(),
  });
};

const pruneExchangedCodeResultCache = () => {
  const now = Date.now();
  exchangedCodeResultCache.forEach((entry, key) => {
    if (!entry || now - Number(entry.createdAt || 0) > EXCHANGED_CODE_CACHE_TTL_MS) {
      exchangedCodeResultCache.delete(key);
    }
  });
};

const readExchangedCodeResultCache = (code) => {
  pruneExchangedCodeResultCache();
  return exchangedCodeResultCache.get(code)?.tokens || null;
};

const writeExchangedCodeResultCache = (code, tokens) => {
  exchangedCodeResultCache.set(code, {
    tokens,
    createdAt: Date.now(),
  });
};

const appendPendingState = (storage, pendingState) => {
  const current = prunePendingStates(readPendingStates(storage));
  const next = [...current.filter((entry) => entry.state !== pendingState.state), pendingState].slice(-10);
  writePendingStates(storage, next);
};

const consumePendingStateFromStorage = (storage, stateFromCallback) => {
  const current = prunePendingStates(readPendingStates(storage));
  let matched = null;
  const next = [];

  current.forEach((entry) => {
    if (!matched && entry.state === stateFromCallback) {
      matched = entry;
      return;
    }
    next.push(entry);
  });

  writePendingStates(storage, next);
  return matched;
};

const readStoredTokens = () =>
  safeJsonParse(readAuthSessionItem(TOKEN_STORAGE_KEY), {
    accessToken: '',
    refreshToken: '',
  });

export const getStoredAccessToken = () => (readStoredTokens().accessToken || '').trim();
export const getStoredRefreshToken = () => (readStoredTokens().refreshToken || '').trim();

export const savePcAuthTokens = ({ accessToken, refreshToken }) => {
  writeAuthSessionItem(
    TOKEN_STORAGE_KEY,
    JSON.stringify({
      accessToken: (accessToken || '').trim(),
      refreshToken: (refreshToken || '').trim(),
    })
  );
};

export const clearPcAuthTokens = () => {
  removeAuthSessionItem(TOKEN_STORAGE_KEY);
  removeAuthSessionItem(USER_STORAGE_KEY);
  clearSessionLoginAt();
};


export const clearExpiredPcAuthSession = () => {
  clearPcAuthTokens();
  localStorage.removeItem('usid');
  localStorage.removeItem('usid_expiry');
};
export const clearAllUserSession = (options = {}) => {
  const { preservePendingAuthState = false } = options;
  removeAuthSessionItem(TOKEN_STORAGE_KEY);
  removeAuthSessionItem(USER_STORAGE_KEY);
  localStorage.removeItem('usid');
  localStorage.removeItem('usid_expiry');
  localStorage.removeItem('session_login_at');
  localStorage.removeItem('selected-client');

  try {
    sessionStorage.removeItem('pcAuthLoginMethod');
  } catch (error) {
    // Best effort storage cleanup.
  }

  if (!preservePendingAuthState) {
    localStorage.removeItem(PENDING_STATES_STORAGE_KEY);
    localStorage.removeItem(LEGACY_PENDING_STATE_STORAGE_KEY);
    try {
      sessionStorage.removeItem(PENDING_STATES_STORAGE_KEY);
      sessionStorage.removeItem(LEGACY_PENDING_STATE_STORAGE_KEY);
    } catch (error) {
      // Best effort storage cleanup.
    }
  }
};

// ── Post-login redirect helpers ──
// The route guards store the protected path an unauthenticated user tried to
// reach in `localStorage.intendedRoute`. Only internal Dashboard paths are
// valid redirect targets — this rejects open-redirects (absolute or
// protocol-relative URLs), non-Dashboard paths, and the auth callback route
// (which would loop).
export const isSafePostLoginRoute = (route) => {
  const value = (route || '').trim();
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return false;
  }
  if (!value.startsWith('/Dashboard')) {
    return false;
  }
  return value !== '/Dashboard/Callback';
};

// Peek at the stored intended route without clearing it.
export const readIntendedRoute = () => (localStorage.getItem(INTENDED_ROUTE_KEY) || '').trim();

// Read and clear the stored intended route, returning it only when it is a
// safe internal Dashboard target (otherwise '').
export const consumeIntendedRoute = () => {
  const intendedRoute = readIntendedRoute();
  localStorage.setItem(INTENDED_ROUTE_KEY, '');
  return isSafePostLoginRoute(intendedRoute) ? intendedRoute : '';
};

// Resolve the best post-login destination: an explicit returnTo wins, then the
// stored intended route, then the default landing page. Always consumes the
// stored intended route so it does not leak into a later login.
export const resolvePostLoginRoute = (returnTo) => {
  const intendedRoute = consumeIntendedRoute();
  if (isSafePostLoginRoute(returnTo)) {
    return returnTo.trim();
  }
  if (intendedRoute) {
    return intendedRoute;
  }
  return DEFAULT_POST_LOGIN_ROUTE;
};

export const getStoredPcAuthUser = () => safeJsonParse(readAuthSessionItem(USER_STORAGE_KEY), null);

const savePcAuthUser = (user) => {
  if (!user || typeof user !== 'object') {
    removeAuthSessionItem(USER_STORAGE_KEY);
    return;
  }
  writeAuthSessionItem(USER_STORAGE_KEY, JSON.stringify(user));
};

const parseJwtPayload = (token) => {
  if (!token || token.split('.').length < 2) {
    return null;
  }
  try {
    const payloadSegment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload =
      payloadSegment + '='.repeat((4 - (payloadSegment.length % 4 || 4)) % 4);
    const decoded = atob(paddedPayload);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

const isTokenUsable = (token) => {
  const payload = parseJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return Number(payload.exp) - nowSeconds > 60;
};

const requestJson = async (url, options) => {
  const response = await fetch(url, options);
  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : {};
  return { response, data };
};

const fetchUserInfo = async (accessToken) => {
  if (!accessToken) {
    return null;
  }
  const userInfoUrl = resolvePcAuthApiUrl('/auth/userinfo');
  try {
    const { response, data } = await requestJson(userInfoUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

export const refreshDirectAccessToken = async () => {
  if (isStrictSessionExpired()) {
    clearExpiredPcAuthSession();
    return '';
  }
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return '';
  }
  const refreshUrl = resolvePcAuthApiUrl('/auth/token/refresh');

  const attemptRefresh = async () => {
    const { response, data } = await requestJson(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });
    return { response, data };
  };

  try {
    let { response, data } = await attemptRefresh();

    // Retry once on transient failure before wiping tokens
    if (!response.ok) {
      console.log('⚠️ Token refresh failed, retrying once...');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      ({ response, data } = await attemptRefresh());
    }

    if (!response.ok) {
      console.log('❌ Token refresh failed after retry — clearing tokens');
      clearPcAuthTokens();
      return '';
    }

    const nextAccessToken = data?.accessToken || data?.access_token || '';
    const nextRefreshToken = data?.refreshToken || data?.refresh_token || refreshToken;

    if (!nextAccessToken || !nextRefreshToken) {
      clearPcAuthTokens();
      return '';
    }

    savePcAuthTokens({
      accessToken: nextAccessToken,
      refreshToken: nextRefreshToken,
    });
    return nextAccessToken;
  } catch (error) {
    // Network error — retry once before giving up
    try {
      console.log('⚠️ Token refresh network error, retrying once...');
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const { response, data } = await attemptRefresh();
      if (response.ok) {
        const nextAccessToken = data?.accessToken || data?.access_token || '';
        const nextRefreshToken = data?.refreshToken || data?.refresh_token || refreshToken;
        if (nextAccessToken && nextRefreshToken) {
          savePcAuthTokens({ accessToken: nextAccessToken, refreshToken: nextRefreshToken });
          return nextAccessToken;
        }
      }
    } catch (_) {
      // Retry also failed
    }
    return '';
  }
};
const refreshDirectAccessTokenSingleFlight = async () => {
  if (!refreshInFlightPromise) {
    refreshInFlightPromise = refreshDirectAccessToken().finally(() => {
      refreshInFlightPromise = null;
    });
  }
  return refreshInFlightPromise;
};

export const getValidDirectAccessToken = async () => {
  if (isStrictSessionExpired()) {
    clearExpiredPcAuthSession();
    return '';
  }
  const accessToken = getStoredAccessToken();
  if (accessToken && isTokenUsable(accessToken)) {
    return accessToken;
  }
  return refreshDirectAccessTokenSingleFlight();
};

export const loadDirectSession = async () => {
  if (isStrictSessionExpired()) {
    clearExpiredPcAuthSession();
    return null;
  }
  let accessToken = await getValidDirectAccessToken();

  if (!accessToken) {
    return null;
  }

  let user = await fetchUserInfo(accessToken);
  if (!user) {
    accessToken = await refreshDirectAccessTokenSingleFlight();
    if (!accessToken) {
      return null;
    }
    user = await fetchUserInfo(accessToken);
  }

  if (!user) {
    return null;
  }

  savePcAuthUser(user);
  return {
    user,
    accessToken,
  };
};

export const logoutDirectSession = async () => {
  const accessToken = getStoredAccessToken();
  if (accessToken) {
    const logoutUrl = resolvePcAuthApiUrl('/auth/logout');
    try {
      await fetch(logoutUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch (error) {
      // Best effort logout.
    }
  }

  clearPcAuthTokens();
};

export const beginDirectSso = ({ provider, email, domain, tenant, returnTo }) => {
  const providerSlug = getProviderSlug(provider);
  if (!providerSlug) {
    throw new Error('Unsupported provider.');
  }

  const normalizedEmail = normalizeEmail(email);
  const resolvedDomain = (domain || normalizedEmail.split('@')[1] || '').trim().toLowerCase();
  const resolvedTenant = (tenant || '').trim().toLowerCase() || inferTenant(resolvedDomain);
  const state = makeStateToken();
  const pendingState = {
    state,
    returnTo: (returnTo || '').trim(),
    email: normalizedEmail,
    createdAt: Date.now(),
  };
  appendPendingState(localStorage, pendingState);
  appendPendingState(sessionStorage, pendingState);

  const ssoUrl = new URL(resolveAbsoluteUrl('', `/auth/sso/${providerSlug}`));
  ssoUrl.searchParams.set('tenant', resolvedTenant);
  ssoUrl.searchParams.set('redirect_uri', getPcAuthRedirectUri());
  ssoUrl.searchParams.set('state', state);
  if (normalizedEmail) {
    ssoUrl.searchParams.set('email', normalizedEmail);
  }

  window.location.assign(ssoUrl.toString());
};

export const consumePendingState = (stateFromCallback) => {
  const normalizedState = (stateFromCallback || '').trim();
  if (!normalizedState) {
    return { ok: false, error: 'state_missing' };
  }

  const consumedState = readConsumedStateCache(normalizedState);
  if (consumedState) {
    return {
      ok: true,
      returnTo: consumedState.returnTo,
      replayed: true,
    };
  }
  const sessionMatch = consumePendingStateFromStorage(sessionStorage, normalizedState);
  if (sessionMatch) {
    const returnTo = (sessionMatch.returnTo || '').trim();
    writeConsumedStateCache(normalizedState, returnTo);
    return {
      ok: true,
      returnTo,
    };
  }
  const localMatch = consumePendingStateFromStorage(localStorage, normalizedState);
  if (localMatch) {
    const returnTo = (localMatch.returnTo || '').trim();
    writeConsumedStateCache(normalizedState, returnTo);
    return {
      ok: true,
      returnTo,
    };
  }
  const sessionPending = prunePendingStates(readPendingStates(sessionStorage));
  const localPending = prunePendingStates(readPendingStates(localStorage));
  const hasPending = sessionPending.length > 0 || localPending.length > 0;
  const fallbackReturnTo = (sessionPending[0]?.returnTo || localPending[0]?.returnTo || '').trim();

  if (isLocalhostRuntime()) {
    return {
      ok: true,
      returnTo: fallbackReturnTo,
      bypassed: true,
      error: hasPending ? 'state_mismatch' : 'state_missing',
    };
  }

  return { ok: false, error: hasPending ? 'state_mismatch' : 'state_missing' };
};

export const exchangeAuthCodeForSession = async (code) => {
  const normalizedCode = (code || '').trim();
  if (!normalizedCode) {
    return null;
  }

  const cachedTokens = readExchangedCodeResultCache(normalizedCode);
  if (cachedTokens) {
    return cachedTokens;
  }

  const inFlightExchange = exchangeCodeInFlightCache.get(normalizedCode);
  if (inFlightExchange) {
    return inFlightExchange;
  }

  const exchangePromise = (async () => {
    const exchangeUrl = resolvePcAuthApiUrl('/auth/token/exchange');
    const { response, data } = await requestJson(exchangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: normalizedCode,
        state: (new URLSearchParams(window.location.search).get('state') || '').trim(),
        redirectUri: getPcAuthRedirectUri(),
      }),
    });

    if (!response.ok) {
      console.warn('pc-auth code exchange failed', {
        status: response.status,
        error: data?.error || data?.message || null,
      });
      return null;
    }

    const accessToken = data?.accessToken || data?.access_token || '';
    const refreshToken = data?.refreshToken || data?.refresh_token || '';
    if (!accessToken || !refreshToken) {
      return null;
    }

    savePcAuthTokens({ accessToken, refreshToken });
    markSessionLoginAtNow();
    const user = await fetchUserInfo(accessToken);
    if (user) {
      savePcAuthUser(user);
    }

    const exchangedTokens = {
      accessToken,
      refreshToken,
      user,
    };

    writeExchangedCodeResultCache(normalizedCode, exchangedTokens);
    return exchangedTokens;
  })().finally(() => {
    exchangeCodeInFlightCache.delete(normalizedCode);
  });

  exchangeCodeInFlightCache.set(normalizedCode, exchangePromise);
  return exchangePromise;
};
